import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle, Loader2, XCircle, ArrowRight } from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './PaymentSuccess.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function PaymentSuccess({ user, onLogout }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    } else {
      setStatus('failed');
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId, attemptCount = 0) => {
    if (attemptCount >= maxAttempts) {
      setStatus('failed');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
      } else if (response.data.status === 'expired') {
        setStatus('failed');
      } else {
        // Continue polling
        setAttempts(attemptCount + 1);
        setTimeout(() => {
          checkPaymentStatus(sessionId, attemptCount + 1);
        }, 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attemptCount < maxAttempts - 1) {
        setTimeout(() => {
          checkPaymentStatus(sessionId, attemptCount + 1);
        }, 2000);
      } else {
        setStatus('failed');
      }
    }
  };

  const handleContinue = () => {
    navigate('/dashboard');
    window.location.reload(); // Refresh to update user subscription
  };

  return (
    <div className="payment-success-page" data-testid="payment-success-page">
      <div className="payment-card">
        {status === 'checking' && (
          <div className="status-content fade-in" data-testid="status-checking">
            <Loader2 size={64} className="spinner" />
            <h1>Processing Payment...</h1>
            <p>Please wait while we verify your payment</p>
            <div className="progress-dots">
              <span className="dot"></span>
              <span className="dot"></span>
              <span className="dot"></span>
            </div>
          </div>
        )}

        {status === 'success' && (
          <div className="status-content fade-in" data-testid="status-success">
            <CheckCircle size={64} className="success-icon" />
            <h1>Payment Successful!</h1>
            <p>Your subscription has been activated successfully</p>
            <button className="continue-btn" onClick={handleContinue}>
              Continue to Dashboard
              <ArrowRight size={18} />
            </button>
          </div>
        )}

        {status === 'failed' && (
          <div className="status-content fade-in" data-testid="status-failed">
            <XCircle size={64} className="error-icon" />
            <h1>Payment Failed</h1>
            <p>
              We couldn't verify your payment. Please contact support if money was deducted.
            </p>
            <button className="continue-btn" onClick={handleContinue}>
              Return to Dashboard
              <ArrowRight size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PaymentSuccess;